import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Select from 'react-select';
import './Factures.css';

export default function NouveauBonLivraison() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [loadingFacture, setLoadingFacture] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [factures, setFactures] = useState([]);
    const [filteredFactures, setFilteredFactures] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [searchClient, setSearchClient] = useState("");
    const [searchFacture, setSearchFacture] = useState("");
    
    // Toast notification
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });

    const queryParams = new URLSearchParams(location.search);
    const factureId = queryParams.get('facture');
    
    const [bl, setBl] = useState({
        id_client: '',
        id_facture: factureId || '',
        date_livraison: new Date().toISOString().split('T')[0],
        adresse_livraison: '',
        destinataire: '',
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: "", type: "success" });
        }, 4000);
    };

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchFactures();
        
        if (factureId) {
            chargerFacture(factureId);
        }
    }, [factureId]);

    // Filtrer les factures par client sélectionné
    useEffect(() => {
        if (bl.id_client) {
            const facturesFiltrees = factures.filter(f => f.id_client === parseInt(bl.id_client));
            setFilteredFactures(facturesFiltrees);
            // Si la facture sélectionnée n'appartient plus au client, on la réinitialise
            if (bl.id_facture && !facturesFiltrees.some(f => f.id_facture === parseInt(bl.id_facture))) {
                setBl(prev => ({ ...prev, id_facture: '' }));
            }
        } else {
            setFilteredFactures([]);
        }
    }, [bl.id_client, factures]);

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setProduits(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const fetchFactures = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?statut=payee`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setFactures(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const chargerFacture = async (id) => {
        try {
            setLoadingFacture(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement facture");
            
            const result = await response.json();
            const factureData = result.data;
            
            if (factureData) {
                setBl({
                    ...bl,
                    id_client: factureData.id_client.toString(),
                    id_facture: id,
                    adresse_livraison: factureData.client_adresse || '',
                    destinataire: factureData.client_nom,
                    notes: `Bon de livraison généré depuis la facture ${factureData.numero_facture}`,
                    lignes: factureData.lignes?.map(ligne => ({
                        id_produit: ligne.id_produit,
                        reference: ligne.reference,
                        designation: ligne.designation,
                        quantite: ligne.quantite,
                        lot: '',
                        date_peremption: ''
                    })) || []
                });
            }
        } catch (err) {
            console.error("Erreur chargement facture:", err);
            setError("Erreur lors du chargement de la facture");
        } finally {
            setLoadingFacture(false);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            lot: '',
            date_peremption: ''
        };
        setBl({
            ...bl,
            lignes: [...bl.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
        showToast("Produit ajouté avec succès", "success");
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = bl.lignes.filter((_, i) => i !== index);
        setBl({ ...bl, lignes: nouvellesLignes });
        showToast("Produit supprimé", "info");
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...bl.lignes];
        nouvellesLignes[index][champ] = valeur;
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!bl.id_client) {
            showToast("Veuillez sélectionner un client", "error");
            return;
        }
        
        if (bl.lignes.length === 0) {
            showToast("Veuillez ajouter au moins un produit", "error");
            return;
        }

        setSaving(true);

        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                showToast("Utilisateur non connecté", "error");
                return;
            }

            const user = JSON.parse(userStr);
            const id_utilisateur = user.id || user.id_utilisateur;
            
            if (!id_utilisateur) {
                showToast("ID utilisateur non trouvé", "error");
                return;
            }

            const token = localStorage.getItem("token");
            
            const payload = {
                action: 'create',
                ...bl,
                id_utilisateur: id_utilisateur
            };

            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast("Bon de livraison créé avec succès !", "success");
                setTimeout(() => {
                    navigate('/ventes/blListe');
                }, 2000);
            } else {
                showToast(result.message || "Erreur lors de la création", "error");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showToast("Erreur de connexion au serveur", "error");
        } finally {
            setSaving(false);
        }
    };

    // Options pour React Select
    const clientOptions = clients.map(client => ({
        value: client.id_client,
        label: `${client.raison_sociale} (${client.code_client})`,
        adresse: client.adresse,
        raison_sociale: client.raison_sociale
    }));

    const factureOptions = filteredFactures.map(facture => ({
        value: facture.id_facture,
        label: `${facture.numero_facture} - ${formatCurrency(facture.net_a_payer)}`,
        numero: facture.numero_facture
    }));

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderWidth: '2px',
            borderColor: state.isFocused ? '#21ACCD' : '#e2e8f0',
            borderRadius: '12px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(33, 172, 205, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#21ACCD'
            }
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected ? '#21ACCD' : isFocused ? '#e8f0fe' : 'white',
            color: isSelected ? 'white' : '#1e293b',
            '&:active': {
                backgroundColor: '#21ACCD'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#94a3b8'
        })
    };

    if (loadingFacture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-truck-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement de la facture</h5>
                            <p className="text-muted small mb-0">Récupération des données...</p>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #21ACCD, #1a8ba8); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #21ACCD; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                
                {/* Toast Notification */}
                {toast.show && (
                    <div className="toast-notification toast-notification-show">
                        <div className={`toast-content toast-${toast.type}`}>
                            <div className="toast-icon">
                                {toast.type === 'success' ? (
                                    <i className="ri-checkbox-circle-line"></i>
                                ) : toast.type === 'error' ? (
                                    <i className="ri-error-warning-line"></i>
                                ) : (
                                    <i className="ri-information-line"></i>
                                )}
                            </div>
                            <div className="toast-message">
                                <h6>{toast.type === 'success' ? 'Succès' : toast.type === 'error' ? 'Erreur' : 'Information'}</h6>
                                <p>{toast.message}</p>
                            </div>
                            <button className="toast-close" onClick={() => setToast({ show: false, message: "", type: "success" })}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="toast-progress"></div>
                    </div>
                )}

                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-truck-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">
                                        {factureId ? 'Créer un BL depuis une facture' : 'Nouveau bon de livraison'}
                                    </h4>
                                    <p className="text-muted small mb-0">
                                        {factureId ? 'Les données de la facture ont été pré-remplies' : 'Remplissez les informations pour créer un bon de livraison'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                as={Link} 
                                to="/ventes/blListe"
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={clientOptions.find(option => option.value === parseInt(bl.id_client))}
                                                        onChange={(selected) => {
                                                            const client = clients.find(c => c.id_client === selected?.value);
                                                            setBl({
                                                                ...bl,
                                                                id_client: selected?.value || '',
                                                                adresse_livraison: client?.adresse || '',
                                                                destinataire: client?.raison_sociale || ''
                                                            });
                                                        }}
                                                        placeholder="Rechercher un client..."
                                                        isClearable
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => "Aucun client trouvé"}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Facture associée</Form.Label>
                                                    <Select
                                                        options={factureOptions}
                                                        value={factureOptions.find(option => option.value === parseInt(bl.id_facture))}
                                                        onChange={(selected) => {
                                                            const idFacture = selected?.value;
                                                            setBl({...bl, id_facture: idFacture || ''});
                                                            if (idFacture) {
                                                                chargerFacture(idFacture);
                                                            }
                                                        }}
                                                        placeholder={bl.id_client ? "Rechercher une facture..." : "Sélectionnez d'abord un client"}
                                                        isClearable
                                                        isDisabled={!bl.id_client}
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => bl.id_client ? "Aucune facture pour ce client" : "Sélectionnez d'abord un client"}
                                                    />
                                                    {!bl.id_client && (
                                                        <Form.Text className="text-muted small">
                                                            Veuillez d'abord sélectionner un client
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date livraison <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={bl.date_livraison}
                                                        onChange={(e) => setBl({...bl, date_livraison: e.target.value})}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Destinataire</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={bl.destinataire}
                                                        onChange={(e) => setBl({...bl, destinataire: e.target.value})}
                                                        placeholder="Nom du destinataire"
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Adresse de livraison</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.adresse_livraison}
                                                        onChange={(e) => setBl({...bl, adresse_livraison: e.target.value})}
                                                        placeholder="Adresse complète de livraison"
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.notes}
                                                        onChange={(e) => setBl({...bl, notes: e.target.value})}
                                                        placeholder="Instructions particulières..."
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes du BL */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits à livrer
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {bl.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center">Quantité</th>
                                                            <th>Lot</th>
                                                            <th>Date péremption</th>
                                                            <th className="text-center pe-4"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bl.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td className="ps-4"><span className="reference-badge">{ligne.reference}</span></td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        className="text-center"
                                                                    />
                                                                </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={ligne.lot || ''}
                                                                        onChange={(e) => updateLigne(index, 'lot', e.target.value)}
                                                                        placeholder="N° lot"
                                                                        size="sm"
                                                                    />
                                                                </td>
                                                                <td style={{ width: '130px' }}>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={ligne.date_peremption || ''}
                                                                        onChange={(e) => updateLigne(index, 'date_peremption', e.target.value)}
                                                                        size="sm"
                                                                    />
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                    className="rounded-pill px-4"
                                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-item d-flex justify-content-between mb-3 pb-2 border-bottom">
                                            <span className="text-muted">Total articles</span>
                                            <span className="fw-bold fs-4">{bl.lignes.length}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-4 pb-2 border-bottom">
                                            <span className="text-muted">Quantité totale</span>
                                            <span className="fw-bold fs-4">
                                                {bl.lignes.reduce((acc, l) => acc + (l.quantite || 0), 0)}
                                            </span>
                                        </div>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Statut</Form.Label>
                                            <Form.Select
                                                value={bl.statut}
                                                onChange={(e) => setBl({...bl, statut: e.target.value})}
                                                className="border-2 rounded-3"
                                            >
                                                <option value="brouillon">Brouillon</option>
                                                <option value="prepare">Préparé</option>
                                            </Form.Select>
                                        </Form.Group>

                                        <hr />

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving}
                                                className="rounded-pill py-2"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Créer le BL
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/blListe')}
                                                className="rounded-pill"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <InputGroup className="mb-3">
                        <InputGroup.Text className="bg-light">
                            <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit..."
                            className="rounded-pill"
                            value={searchClient}
                            onChange={(e) => setSearchClient(e.target.value)}
                        />
                    </InputGroup>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits
                                    .filter(p => 
                                        p.reference?.toLowerCase().includes(searchClient.toLowerCase()) ||
                                        p.designation?.toLowerCase().includes(searchClient.toLowerCase())
                                    )
                                    .map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">
                                            <span className={produit.stock_actuel < 1 ? 'text-danger fw-bold' : 'text-muted'}>
                                                {produit.stock_actuel || 0}
                                            </span>
                                        </td>
                                        <td className="pe-4">
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                disabled={produit.stock_actuel < 1}
                                                className="rounded-circle"
                                                style={{ background: '#21ACCD', border: 'none', width: '32px', height: '32px' }}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                /* React Select Styles */
                .react-select-container {
                    width: 100%;
                }
                .react-select__control {
                    min-height: 38px;
                }
                .react-select__placeholder {
                    color: #94a3b8;
                }
                .react-select__indicator-separator {
                    display: none;
                }
                .react-select__clear-indicator {
                    color: #94a3b8;
                    cursor: pointer;
                }
                .react-select__clear-indicator:hover {
                    color: #ef4444;
                }
                
                /* Toast Notifications */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    min-width: 350px;
                    max-width: 450px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    overflow: hidden;
                    transform: translateX(120%);
                    transition: transform 0.3s ease-out;
                    opacity: 0;
                }
                .toast-notification-show {
                    transform: translateX(0);
                    opacity: 1;
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px 20px;
                    position: relative;
                }
                .toast-success { border-left: 4px solid #10b981; }
                .toast-error { border-left: 4px solid #ef4444; }
                .toast-info { border-left: 4px solid #0dcaf0; }
                .toast-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                .toast-success .toast-icon { background: #ecfdf5; color: #10b981; }
                .toast-error .toast-icon { background: #fef2f2; color: #ef4444; }
                .toast-info .toast-icon { background: #e2f3f5; color: #0dcaf0; }
                .toast-message { flex: 1; }
                .toast-message h6 { margin: 0 0 4px 0; font-size: 0.9rem; font-weight: 600; }
                .toast-message p { margin: 0; font-size: 0.8rem; color: #64748b; }
                .toast-close {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }
                .toast-close:hover { color: #1e293b; }
                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    width: 100%;
                    background: rgba(0,0,0,0.05);
                    animation: progress 4s linear forwards;
                }
                .toast-success .toast-progress { background: linear-gradient(90deg, #10b981, #059669); }
                .toast-error .toast-progress { background: linear-gradient(90deg, #ef4444, #dc2626); }
                .toast-info .toast-progress { background: linear-gradient(90deg, #0dcaf0, #0ea5e9); }
                @keyframes progress {
                    0% { width: 100%; }
                    100% { width: 0%; }
                }
                @media (max-width: 768px) {
                    .toast-notification {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                        min-width: auto;
                        max-width: none;
                    }
                }
                
                .product-modal .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-16 {
                    font-size: 1rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}